// third-party
import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../index';
import apiSlice from './apiSlice';

const initialState = {
    error: null,
    projects: []
};

const slice = createSlice({
    name: 'project',
    initialState,
    reducers: {
        getProject(state, action) {
            state.projects = action.payload;
        },
        getTable(state, action) {
            state.tables = action.payload;
        },
        getDocument(state, action) {
            state.documents = action.payload;
        },
        getAirflowDags(state, action) {
            state.dags = action.payload;
        },
        getAirflowDagsName(state, action) {
            state.dags_name = action.payload;
        },
        getAirflowDagsNodes(state, action) {
            state.dags_nodes = action.payload;
        },
        getAirflowDagsEdges(state, action) {
            state.dags_edges = action.payload;
        },
        hasError(state, action) {
            state.error = action.payload;
        }
    }
});

export default slice.reducer;

/** Action creator: cập nhật danh sách project (dùng sau khi xóa để cập nhật UI ngay) */
export const setProjectsList = slice.actions.getProject;

/**
 * @param {boolean} [forceRefetch] - Sau khi xóa project, truyền true để ép request mới (tránh cache còn project vừa xóa)
 */
export function fetchProjects(forceRefetch = false) {
    return async (dispatch) => {
        try {
            const userToken = localStorage.getItem('user_token');
            const arg = forceRefetch ? { userToken: userToken || undefined, refetch: Date.now() } : (userToken || undefined);
            const result = await dispatch(apiSlice.endpoints.getProjects.initiate(arg)).unwrap();
            const projects = Array.isArray(result) ? result : (result?.projects ?? []);
            dispatch(slice.actions.getProject(projects));
        } catch (error) {
            console.error('Error fetching projects:', error);
            dispatch(slice.actions.hasError(error));
            dispatch(slice.actions.getProject([]));
        }
    };
}

/**
 * @param {string} project_name
 * @param {string} description
 * @param {string} [tenant_admin_email] - Chỉ Superadmin: email đăng nhập của tenant admin (bắt buộc khi tạo project cho tenant)
 * @param {string} [tenant_admin_password] - Chỉ Superadmin: mật khẩu đăng nhập cho tenant (bắt buộc khi tạo project cho tenant)
 */
export function addProjects(project_name, description, tenant_admin_email, tenant_admin_password) {
    return async (dispatch) => {
        try {
            const body = { project_name, description };
            if (tenant_admin_email && tenant_admin_email.trim()) {
                body.tenant_admin_email = tenant_admin_email.trim();
                // Luôn gửi password khi có email (tạo user mới hoặc cập nhật)
                body.tenant_admin_password = (tenant_admin_password != null && String(tenant_admin_password).trim()) ? String(tenant_admin_password).trim() : '';
            }
            await dispatch(apiSlice.endpoints.createProject.initiate(body)).unwrap();
            const userToken = localStorage.getItem('user_token');
            const list = await dispatch(apiSlice.endpoints.getProjects.initiate(userToken || undefined)).unwrap();
            const projects = Array.isArray(list) ? list : (list?.projects ?? []);
            dispatch(slice.actions.getProject(projects));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function fetchProjectTables(project_name) {
    return async (dispatch) => {
        try {
            const result = await dispatch(
                apiSlice.endpoints.getProjectMongodbTables.initiate(project_name)
            ).unwrap();
            dispatch(slice.actions.getTable(result));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function createProjectTables(project_name, table_name, table_desc) {
    return async (dispatch) => {
        try {
            const user_token = localStorage.getItem('user_token');
            const formData = new FormData();
            formData.append('user_token', user_token);
            formData.append('project_name', project_name);
            formData.append('table_name', table_name);
            formData.append('table_desc', table_desc);
            const result = await dispatch(
                apiSlice.endpoints.createProjectMongodbTables.initiate(formData)
            ).unwrap();
            dispatch(slice.actions.getTable(result));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function deleteProjectTables(project_name, table_name) {
    return async (dispatch) => {
        try {
            const user_token = localStorage.getItem('user_token');
            const formData = new FormData();
            formData.append('user_token', user_token);
            formData.append('project_name', project_name);
            formData.append('table_name', table_name);
            const result = await dispatch(
                apiSlice.endpoints.deleteProjectMongodbTables.initiate(formData)
            ).unwrap();
            dispatch(slice.actions.getTable(result));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function removeProjectTables() {
    return () => {
        dispatch(slice.actions.getTable(null));
    };
}

export function fetchProjectDocuments(project_name, table_name) {
    return async (dispatch) => {
        try {
            const user_token = localStorage.getItem('user_token');
            const result = await dispatch(
                apiSlice.endpoints.getProjectMongodbDocuments.initiate({
                    user_token,
                    project_name,
                    table_name
                })
            ).unwrap();
            dispatch(slice.actions.getDocument(result));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function updateProjectDocuments(project_name, table_name, row) {
    return async (dispatch) => {
        try {
            const user_token = localStorage.getItem('user_token');
            const formData = new FormData();
            formData.append('user_token', user_token);
            formData.append('project_name', project_name);
            formData.append('table_name', table_name);
            formData.append('row', JSON.stringify(row));
            await dispatch(
                apiSlice.endpoints.updateProjectMongodbDocuments.initiate(formData)
            ).unwrap();
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function deleteProjectDocuments(project_name, table_name, row_id) {
    return async (dispatch) => {
        try {
            const user_token = localStorage.getItem('user_token');
            const formData = new FormData();
            formData.append('user_token', user_token);
            formData.append('project_name', project_name);
            formData.append('table_name', table_name);
            formData.append('row_id', row_id);
            await dispatch(
                apiSlice.endpoints.deleteProjectMongodbDocuments.initiate(formData)
            ).unwrap();
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function removeProjectDocuments() {
    return () => {
        dispatch(slice.actions.getDocument(null));
    };
}

export function createProjectJupyterhub() {
    return async () => {};
}

export function deleteProjectJupyterhub(username) {
    return async (dispatch) => {
        try {
            const result = await dispatch(
                apiSlice.endpoints.deleteHubUser.initiate(username)
            ).unwrap();
            dispatch(slice.actions.getDocument(result));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

function generatePassword(user_token) {
    let chars = user_token;
    let generatedPassword = '';
    for (let i = 0; i < chars.length / 2; i++) {
        generatedPassword += chars[i + 3];
    }
    return generatedPassword;
}

export function createProjectAirflow(project_name) {
    return async (dispatch) => {
        try {
            const user_token = localStorage.getItem('user_token');
            const email = localStorage.getItem('user_email');
            const formData = new FormData();
            formData.append('user_token', user_token);
            formData.append('project_name', project_name);
            formData.append('email', email);
            formData.append('jupyter_password', generatePassword(user_token));
            const result = await dispatch(
                apiSlice.endpoints.createAirUser.initiate(formData)
            ).unwrap();
            dispatch(slice.actions.getDocument(result));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function deleteProjectAirflow(project_name) {
    return async (dispatch) => {
        try {
            const user_token = localStorage.getItem('user_token');
            const formData = new FormData();
            formData.append('user_token', user_token);
            formData.append('project_name', project_name);
            const result = await dispatch(
                apiSlice.endpoints.deleteAirUser.initiate(formData)
            ).unwrap();
            dispatch(slice.actions.getDocument(result));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function fetchProjectAirflowDags(project_name) {
    return async (dispatch) => {
        try {
            const formData = new FormData();
            formData.append('project_name', project_name);
            const result = await dispatch(
                apiSlice.endpoints.getProjectAirDags.initiate(formData)
            ).unwrap();
            dispatch(slice.actions.getAirflowDags(result));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            dispatch(slice.actions.getAirflowDags([]));
        }
    };
}

export function insertProjectAirflowDags(project_name, dag_name, task_name, schedule_interval) {
    return async (dispatch) => {
        try {
            const formData = new FormData();
            formData.append('project_name', project_name);
            formData.append('dag_name', dag_name);
            formData.append('task_name', JSON.stringify(task_name));
            formData.append('schedule_interval', schedule_interval);
            const result = await dispatch(
                apiSlice.endpoints.insertProjectAirDag.initiate(formData)
            ).unwrap();
            dispatch(slice.actions.getAirflowDags(result));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function deleteProjectAirflowDags(project_name, dag_name) {
    return async (dispatch) => {
        try {
            const formData = new FormData();
            formData.append('project_name', project_name);
            formData.append('dag_name', dag_name);
            const result = await dispatch(
                apiSlice.endpoints.deleteProjectAirDag.initiate(formData)
            ).unwrap();
            dispatch(slice.actions.getAirflowDags(result));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getProjectAirflowDags(project_name) {
    return async (dispatch) => {
        try {
            const formData = new FormData();
            formData.append('project_name', project_name);
            const result = await dispatch(
                apiSlice.endpoints.fetchProjectAirDags.initiate(formData)
            ).unwrap();
            dispatch(slice.actions.getAirflowDagsName(result));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getProjectAirflowDagsNodes(project_name, dag_name) {
    return async (dispatch) => {
        try {
            const formData = new FormData();
            formData.append('project_name', project_name);
            formData.append('dag_name', dag_name);
            const result = await dispatch(
                apiSlice.endpoints.fetchProjectAirDagsNodes.initiate(formData)
            ).unwrap();
            dispatch(slice.actions.getAirflowDagsNodes(result));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getProjectAirflowDagsEdges(project_name, dag_name) {
    return async (dispatch) => {
        try {
            const formData = new FormData();
            formData.append('project_name', project_name);
            formData.append('dag_name', dag_name);
            const result = await dispatch(
                apiSlice.endpoints.fetchProjectAirDagsEdges.initiate(formData)
            ).unwrap();
            dispatch(slice.actions.getAirflowDagsEdges(result));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}
